// The scanner's glyphs. Filled paths only; two-tone by role: currentColor carries the
// structure, `fill-primary` carries the subject. A `fill` attribute would beat the
// class, so the accent parts carry none.

import React from 'react';

type Props = { className?: string };

const Svg: React.FC<Props & { children: React.ReactNode }> = ({ className = 'h-6 w-6', children }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
    {children}
  </svg>
);

export const AssemblyIcon: React.FC<Props> = ({ className }) => (
  <Svg className={className}>
    <path fill="currentColor" d="M6 7.4 7.4 6 18 16.6 16.6 18 6 7.4Zm10.6-1.4L18 7.4 7.4 18 6 16.6 16.6 6Z" />
    <path fill="currentColor" fillRule="evenodd" clipRule="evenodd"
      d="M5 1.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7Zm0 1.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm14-1.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7Zm0 1.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4ZM5 15.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7Zm0 1.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm14-1.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7Zm0 1.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z" />
    <rect x="8.75" y="8.75" width="6.5" height="6.5" rx="1.5" className="fill-primary" />
  </Svg>
);

export const MarkerIcon: React.FC<Props> = ({ className }) => (
  <Svg className={className}>
    <path fill="currentColor" fillRule="evenodd" clipRule="evenodd"
      d="M3 4.5A1.5 1.5 0 0 1 4.5 3h7.38a1.5 1.5 0 0 1 1.06.44l7.62 7.62a1.5 1.5 0 0 1 0 2.12l-7.38 7.38a1.5 1.5 0 0 1-2.12 0l-7.62-7.62A1.5 1.5 0 0 1 3 11.88V4.5ZM8 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z" />
    <circle cx="8" cy="8" r="1" className="fill-primary" />
  </Svg>
);

export const LockIcon: React.FC<Props> = ({ className }) => (
  <Svg className={className}>
    <path fill="currentColor" fillRule="evenodd" clipRule="evenodd"
      d="M8 10V7a4 4 0 1 1 8 0v3h-2V7a2 2 0 1 0-4 0v3H8Zm-2 0h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Zm6 3.5a1.5 1.5 0 0 0-.75 2.8V18h1.5v-1.7A1.5 1.5 0 0 0 12 13.5Z" />
  </Svg>
);

export const BackIcon: React.FC<Props> = ({ className }) => (
  <Svg className={className}>
    <path fill="currentColor" d="M14.7 5.3a1 1 0 0 1 0 1.4L9.4 12l5.3 5.3a1 1 0 0 1-1.4 1.4l-6-6a1 1 0 0 1 0-1.4l6-6a1 1 0 0 1 1.4 0Z" />
  </Svg>
);

export const OpenIcon: React.FC<Props> = ({ className }) => (
  <Svg className={className}>
    <path fill="currentColor" d="M9.3 5.3a1 1 0 0 0 0 1.4l5.3 5.3-5.3 5.3a1 1 0 1 0 1.4 1.4l6-6a1 1 0 0 0 0-1.4l-6-6a1 1 0 0 0-1.4 0Z" />
  </Svg>
);

export const WarningIcon: React.FC<Props> = ({ className }) => (
  <Svg className={className}>
    <path fill="currentColor" fillRule="evenodd" clipRule="evenodd"
      d="M10.27 3.9a2 2 0 0 1 3.46 0l8 13.9A2 2 0 0 1 20 21H4a2 2 0 0 1-1.73-3.2l8-13.9ZM11 9v5h2V9h-2Zm0 7v2h2v-2h-2Z" />
  </Svg>
);

export const ScanFrameIcon: React.FC<Props> = ({ className }) => (
  <Svg className={className}>
    <path fill="currentColor"
      d="M3 8V5a2 2 0 0 1 2-2h3v2H5v3H3Zm13-5h3a2 2 0 0 1 2 2v3h-2V5h-3V3ZM3 16h2v3h3v2H5a2 2 0 0 1-2-2v-3Zm16 0h2v3a2 2 0 0 1-2 2h-3v-2h3v-3Z" />
    <rect x="6" y="11" width="12" height="2" rx="1" className="fill-primary" />
  </Svg>
);

export const CheckIcon: React.FC<Props> = ({ className }) => (
  <Svg className={className}>
    <path fill="currentColor" d="M9.55 17.65a1 1 0 0 1-.7-.29l-4.2-4.2a1 1 0 1 1 1.41-1.41l3.5 3.5 8.5-8.5a1 1 0 1 1 1.41 1.41l-9.2 9.2a1 1 0 0 1-.72.29Z" />
  </Svg>
);

export const MinusIcon: React.FC<Props> = ({ className }) => (
  <Svg className={className}>
    <path fill="currentColor" d="M6 11h12a1 1 0 1 1 0 2H6a1 1 0 1 1 0-2Z" />
  </Svg>
);

export const PlusIcon: React.FC<Props> = ({ className }) => (
  <Svg className={className}>
    <path fill="currentColor" d="M11 6a1 1 0 1 1 2 0v5h5a1 1 0 1 1 0 2h-5v5a1 1 0 1 1-2 0v-5H6a1 1 0 1 1 0-2h5V6Z" />
  </Svg>
);

export const ShipIcon: React.FC<Props> = ({ className }) => (
  <Svg className={className}>
    <path fill="currentColor" d="M3 15h2v4h14v-4h2v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4Z" />
    <path className="fill-primary" d="M12 3l5 5h-3.5v7h-3V8H7l5-5Z" />
  </Svg>
);

export const PickIcon: React.FC<Props> = ({ className }) => (
  <Svg className={className}>
    <path fill="currentColor" d="M4 5h9a1 1 0 1 1 0 2H4a1 1 0 0 1 0-2Zm0 6h6a1 1 0 1 1 0 2H4a1 1 0 0 1 0-2Zm0 6h9a1 1 0 1 1 0 2H4a1 1 0 0 1 0-2Z" />
    <path className="fill-primary" d="M17 6a1 1 0 0 1 2 0v8.6l1.3-1.3a1 1 0 0 1 1.4 1.4l-3 3a1 1 0 0 1-1.4 0l-3-3a1 1 0 0 1 1.4-1.4l1.3 1.3V6Z" />
  </Svg>
);
